import { useEffect, useRef } from "react";

const Starfield = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let stars = [];
    let meteors = [];
    let lastMeteor = performance.now() - 3000;
    let raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      const count = Math.min(260, Math.floor((window.innerWidth * window.innerHeight) / 6500));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: (Math.random() * 1.1 + 0.3) * dpr,
        base: Math.random() * 0.5 + 0.25,
        amp: Math.random() * 0.35,
        speed: Math.random() * 0.9 + 0.25,
        phase: Math.random() * Math.PI * 2,
        drift: (Math.random() * 0.03 + 0.005) * dpr,
        gold: Math.random() < 0.06,
      }));
    };

    const tick = (t) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        const alpha = s.base + Math.sin(t * 0.001 * s.speed + s.phase) * s.amp;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.gold
          ? `rgba(212, 175, 55, ${Math.max(alpha, 0.05)})`
          : `rgba(226, 232, 255, ${Math.max(alpha, 0.05)})`;
        ctx.fill();
        s.x -= s.drift;
        if (s.x < -2) s.x = canvas.width + 2;
      }

      if (t - lastMeteor > 8000) {
        lastMeteor = t;
        window.__meteorCount = (window.__meteorCount || 0) + 1;
        const angle = Math.PI * (0.16 + Math.random() * 0.12);
        const speed = (4.5 + Math.random() * 2.5) * dpr;
        const dir = Math.random() < 0.5 ? -1 : 1;
        meteors.push({
          x: dir === 1
            ? canvas.width * (0.05 + Math.random() * 0.4)
            : canvas.width * (0.55 + Math.random() * 0.4),
          y: -20 * dpr,
          vx: dir * Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0,
          maxLife: 100 + Math.random() * 40,
        });
      }
      meteors = meteors.filter(
        (m) => m.life < m.maxLife && m.y < canvas.height + 80 && m.x > -120 && m.x < canvas.width + 120
      );
      for (const m of meteors) {
        m.life++;
        m.x += m.vx;
        m.y += m.vy;
        const fadeIn = Math.min(m.life / 14, 1);
        const fadeOut = 1 - m.life / m.maxLife;
        const alpha = Math.min(fadeIn, Math.max(fadeOut, 0)) * 0.9;
        const tx = m.x - m.vx * 16;
        const ty = m.y - m.vy * 16;
        const grad = ctx.createLinearGradient(m.x, m.y, tx, ty);
        grad.addColorStop(0, `rgba(240, 246, 255, ${alpha})`);
        grad.addColorStop(0.35, `rgba(170, 200, 255, ${alpha * 0.55})`);
        grad.addColorStop(1, "rgba(170, 200, 255, 0)");
        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(tx, ty);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6 * dpr;
        ctx.lineCap = "round";
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(m.x, m.y, 1.6 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };

    resize();
    raf = requestAnimationFrame(tick);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="starfield-canvas"
      className="fixed inset-0 z-0 pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default Starfield;
